import { LayerModule } from '@core/app/constants/layer-module/layer-modules';
import NS from '@core/app/constants/namespaces';
import workareaManager from '@core/app/svgedit/workarea';
import EmbeddedCanvasManager from '@core/app/widgets/FullWindowPanel/EmbeddedCanvasManager';
import { restoreOriginalColors } from '@core/helpers/image/originalColors';
import { getData } from '@core/helpers/layer/layer-config-helper';

import { CUT_COLOR, markRadiusPx, PRINT_AND_CUT_HIDDEN_ATTR } from './constants';
import { contentTags } from './utils/collectContents';
import { getGeneratedCutLayers } from './utils/contentsLayers';
import type { MarkPosition } from './utils/layout';
import type { Point } from './utils/rigidTransform';

export interface BBox {
  height: number;
  width: number;
  x: number;
  y: number;
}

const DESIGN_GROUP_ID = 'print-and-cut-design-group';

export interface PrintAndCutCanvasManagerOptions {
  isResume?: boolean;
  /**
   * Ids of the design elements to keep in the preview; null keeps the whole
   * live design, an empty set drops all artwork (see the store's field of the
   * same name)
   */
  printingContentsElementIds?: null | string[];
}

export class PrintAndCutCanvasManager extends EmbeddedCanvasManager {
  private marksGroup: null | SVGGElement = null;
  private contourPathElem: null | SVGPathElement = null;
  private contourLayerGroup: null | SVGGElement = null;
  private cameraImageElem: null | SVGImageElement = null;
  private contentGroup: null | SVGGElement = null;
  private copiesGroup: null | SVGGElement = null;
  private readonly isResume: boolean;
  private readonly printingContentsElementIds: null | Set<string>;

  constructor({ isResume = false, printingContentsElementIds = null }: PrintAndCutCanvasManagerOptions = {}) {
    super();
    this.printingContentsElementIds = printingContentsElementIds ? new Set(printingContentsElementIds) : null;
    this.isResume = isResume;
  }

  override renderContent = (): void => {
    const svgcontent = document.getElementById('svgcontent') as unknown as SVGSVGElement;

    if (!svgcontent) return;

    this.cameraImageElem = null;
    this.svgcontent = svgcontent.cloneNode(true) as SVGSVGElement;
    this.svgcontent.id = 'print-and-cut-svgcontent';
    // content outside the viewport (e.g. a design larger than the selected
    // paper) must stay visible, like the base setupContent behaves
    this.svgcontent.setAttribute('overflow', 'visible');
    // the preview is display-only: without hit-testing, the editor's inline
    // cursor styles carried over by the clone (e.g. cursor: move on layers)
    // no longer apply
    this.svgcontent.style.pointerEvents = 'none';

    // a cutting layer generated by a previous run is output, not design
    // content: never show it as part of the design preview. Must run before the
    // resume branch below, which force-shows every remaining layer.
    getGeneratedCutLayers(this.svgcontent).forEach((layer) => layer.remove());

    // only UV Print layers are printed (see getContentsLayers): anything else
    // is not part of the sheet and never shows in the preview
    this.svgcontent.querySelectorAll<SVGGElement>('g.layer').forEach((layer) => {
      if (getData(layer, 'module') !== LayerModule.UV_PRINT) layer.remove();
    });

    // On resume the preview must show the sheet as it was printed, not the
    // current canvas: drop everything the printed design did not contain. When
    // the design has changed, the id set is empty and all artwork goes — only
    // the frozen paper, marks and cut path still describe the printed sheet.
    if (this.printingContentsElementIds) {
      const ids = this.printingContentsElementIds;

      this.svgcontent.querySelectorAll<SVGElement>('g.layer > *').forEach((child) => {
        if (contentTags.has(child.tagName) && !ids.has(child.id)) child.remove();
      });
    }

    // force-show: all layers on resume, Finish-tagged ones on fresh runs
    this.svgcontent.querySelectorAll('g.layer').forEach((layer) => {
      if (this.isResume || layer.hasAttribute(PRINT_AND_CUT_HIDDEN_ATTR)) layer.removeAttribute('display');
    });

    // show the artwork as it will be printed, not with the layer color applied
    restoreOriginalColors(this.svgcontent);
    this.root.appendChild(this.svgcontent);

    // the design, its grid copies and the marks live in one group so the
    // alignment transform moves them together, while the camera image below
    // stays fixed
    this.contentGroup = document.createElementNS(NS.SVG, 'g') as SVGGElement;
    this.svgcontent.appendChild(this.contentGroup);

    // one design item (artwork + its cut lines), referenced by the grid copies
    const designGroup = document.createElementNS(NS.SVG, 'g') as SVGGElement;

    designGroup.id = DESIGN_GROUP_ID;

    while (this.svgcontent.firstChild !== this.contentGroup) designGroup.appendChild(this.svgcontent.firstChild!);

    this.contentGroup.appendChild(designGroup);

    this.contourPathElem = document.createElementNS(NS.SVG, 'path') as SVGPathElement;
    this.contourPathElem.setAttribute('fill', 'none');
    this.contourPathElem.setAttribute('stroke', CUT_COLOR);
    this.contourPathElem.setAttribute('stroke-width', '2');
    this.contourPathElem.setAttribute('vector-effect', 'non-scaling-stroke');
    this.contourPathElem.style.display = 'none';
    designGroup.appendChild(this.contourPathElem);

    this.contourLayerGroup = document.createElementNS(NS.SVG, 'g') as SVGGElement;
    designGroup.appendChild(this.contourLayerGroup);

    this.copiesGroup = document.createElementNS(NS.SVG, 'g') as SVGGElement;
    this.contentGroup.appendChild(this.copiesGroup);

    this.marksGroup = document.createElementNS(NS.SVG, 'g') as SVGGElement;
    this.contentGroup.appendChild(this.marksGroup);
  };

  /** Show a copy of the design item (artwork + cut lines) at each non-origin grid offset */
  setGridOffsets = (offsets: Array<{ dx: number; dy: number }>): void => {
    if (!this.copiesGroup) return;

    this.copiesGroup.innerHTML = '';
    offsets
      .filter(({ dx, dy }) => dx !== 0 || dy !== 0)
      .forEach(({ dx, dy }) => {
        const use = document.createElementNS(NS.SVG, 'use') as SVGUseElement;

        use.setAttribute('href', `#${DESIGN_GROUP_ID}`);
        use.setAttribute('transform', `translate(${dx}, ${dy})`);
        this.copiesGroup!.appendChild(use);
      });
  };

  /**
   * Show a camera preview image of the workarea under the design. The camera
   * canvas maps the machine bed with a uniform scale from the machine origin,
   * so the image spans (0, 0)-(width, modelHeight) in content coordinates — a
   * top expansion (e.g. fbm2) extends the canvas into negative y, which the
   * camera never covers, and must not stretch the image.
   */
  setCameraImage = (url: null | string): void => {
    if (!url) {
      this.cameraImageElem?.remove();
      this.cameraImageElem = null;

      return;
    }

    if (!this.svgcontent) return;

    // updated in place so progressive captures swap the picture without
    // re-creating the element
    if (!this.cameraImageElem) {
      const image = document.createElementNS(NS.SVG, 'image') as SVGImageElement;

      // the camera canvas always maps the whole machine bed, independent of
      // the current viewport
      image.setAttribute('x', '0');
      image.setAttribute('y', '0');
      image.setAttribute('width', workareaManager.width.toString());
      image.setAttribute('height', workareaManager.modelHeight.toString());
      this.svgcontent.insertBefore(image, this.contentGroup);
      this.cameraImageElem = image;
    }

    this.cameraImageElem.setAttribute('href', url);
  };

  /**
   * Move the design and overlay by the fitted alignment transform
   * p' = R(angle)·p + (tx, ty), previewing where the cut will land on the
   * captured sheet; the camera image is not affected. Null clears it.
   */
  setContentTransform = (transform: null | { angle: number; tx: number; ty: number }): void => {
    if (!this.contentGroup) return;

    if (!transform) {
      this.contentGroup.removeAttribute('transform');

      return;
    }

    const { angle, tx, ty } = transform;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    this.contentGroup.setAttribute('transform', `matrix(${cos}, ${sin}, ${-sin}, ${cos}, ${tx}, ${ty})`);
  };

  /**
   * Zoom and scroll so the given rect (in content coordinates) is centered,
   * filling `ratio` of the constraining container side (usually the height)
   */
  zoomToBBox = (bbox: BBox, ratio = 0.8): void => {
    if (!this.svgcontent || bbox.width <= 0 || bbox.height <= 0) return;

    const { clientHeight, clientWidth } = this.container;

    this.zoom(ratio * Math.min(clientWidth / bbox.width, clientHeight / bbox.height));

    const [viewX, viewY] = (this.svgcontent.getAttribute('viewBox') ?? '0 0').split(' ').map(Number);
    const contentX = Number.parseFloat(this.svgcontent.getAttribute('x') ?? '0');
    const contentY = Number.parseFloat(this.svgcontent.getAttribute('y') ?? '0');

    this.container.scrollLeft = contentX + (bbox.x + bbox.width / 2 - viewX) * this.zoomRatio - clientWidth / 2;
    this.container.scrollTop = contentY + (bbox.y + bbox.height / 2 - viewY) * this.zoomRatio - clientHeight / 2;
  };

  /** Zoom to the detected mark centers (in content coordinates), padded by the mark radius */
  zoomToMarks = (centers: Point[]): void => {
    if (centers.length === 0) return;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    centers.forEach(({ x, y }) => {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    });

    this.zoomToBBox({
      height: maxY + markRadiusPx - minY,
      width: maxX + markRadiusPx - minX,
      x: minX - markRadiusPx,
      y: minY - markRadiusPx,
    });
  };

  setMarks = (marks: MarkPosition[]): void => {
    if (!this.marksGroup) return;

    this.marksGroup.innerHTML = '';
    // only the black mark is shown: the printed white base disc would be
    // invisible on the white preview background anyway
    marks.forEach(({ cx, cy }) => {
      const circle = document.createElementNS(NS.SVG, 'circle');

      circle.setAttribute('cx', cx.toString());
      circle.setAttribute('cy', cy.toString());
      circle.setAttribute('r', markRadiusPx.toString());
      circle.setAttribute('fill', '#000');
      this.marksGroup!.appendChild(circle);
    });
  };

  setContourPath = (d: null | string): void => {
    if (!this.contourPathElem) return;

    if (d) {
      this.contourPathElem.setAttribute('d', d);
      this.contourPathElem.style.display = 'block';
    } else {
      this.contourPathElem.style.display = 'none';
    }
  };

  /** Highlight the elements used as cut lines (layer / color mode) */
  setContourLayerPaths = (paths: SVGGraphicsElement[]): void => {
    if (!this.contourLayerGroup) return;

    this.contourLayerGroup.innerHTML = '';
    paths.forEach((path) => {
      const clone = path.cloneNode(true) as SVGGraphicsElement;

      clone.setAttribute('fill', 'none');
      clone.setAttribute('stroke', CUT_COLOR);
      clone.setAttribute('stroke-width', '2');
      clone.setAttribute('vector-effect', 'non-scaling-stroke');
      clone.removeAttribute('id');

      // show the vector origin symbol instead of the tinted bitmap symbol
      if (clone.tagName === 'use') {
        const href = clone.getAttribute('xlink:href') || clone.getAttribute('href');
        const originId = href ? document.querySelector(href)?.getAttribute('data-origin-symbol') : null;

        if (originId) clone.setAttribute('xlink:href', `#${originId}`);
      }

      this.contourLayerGroup!.appendChild(clone);
    });
  };

  /**
   * Set the viewport (and its white background) to the given rect: the
   * selected paper, or the padded content area during setup, so the preview
   * looks like the printed sheet. `width`/`height` and the viewBoxes describe
   * the viewport, so `zoom` and `resetView` frame it without further changes.
   * Passing null restores the whole workarea.
   */
  setBackgroundRect = (rect: BBox | null): void => {
    const viewport = rect ?? {
      height: workareaManager.height,
      width: workareaManager.width,
      x: 0,
      y: workareaManager.minY,
    };

    this.width = viewport.width;
    this.height = viewport.height;

    const viewBox = `${viewport.x} ${viewport.y} ${viewport.width} ${viewport.height}`;

    this.background?.setAttribute('viewBox', viewBox);
    this.svgcontent?.setAttribute('viewBox', viewBox);

    // re-anchor the white background rect to the viewport origin; its
    // percentage width/height already resolve against the viewBox
    const backgroundRect = this.background?.querySelector('rect');

    backgroundRect?.setAttribute('x', viewport.x.toString());
    backgroundRect?.setAttribute('y', viewport.y.toString());

    const grid = this.background?.querySelector('#canvasGrid') as null | SVGElement;

    if (grid) grid.style.display = rect ? 'none' : '';
  };
}

export default PrintAndCutCanvasManager;
